import { useEffect, useRef, useState } from "react";

interface UseLoadingStateProps {
  loading: boolean;
  minDisplayTime?: number;
}

export const useDotLoadingState = ({
  loading,
  minDisplayTime = 1000
}: UseLoadingStateProps) => {
  const [showLoading, setShowLoading] = useState(false);
  const loadingStartTimeRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Clear any pending timeout when effect re-runs
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (loading) {
      loadingStartTimeRef.current = Date.now();
      setShowLoading(true);
    } else if (loadingStartTimeRef.current) {
      const elapsedTime = Date.now() - loadingStartTimeRef.current;
      if (elapsedTime < minDisplayTime) {
        const remainingTime = minDisplayTime - elapsedTime;
        timeoutRef.current = setTimeout(() => {
          setShowLoading(false);
          loadingStartTimeRef.current = null;
          timeoutRef.current = null;
        }, remainingTime);
      } else {
        setShowLoading(false);
        loadingStartTimeRef.current = null;
      }
    }

    // Cleanup function to clear timeout on unmount or re-run
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [loading, minDisplayTime]);

  return showLoading;
};
